import React, { useState, useMemo } from 'react';
import { Button, IconButton, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Select from 'react-select';
import DropzoneArea from '../DropzoneArea';
import { styled } from '@mui/material/styles';
import MdbMissingAccountCardsTable from './MdbMissingAccountCardsTable';
import Constants from '../../../../assests/Constants/constants';

const BorderBoxButton = styled(Button)({
    boxSizing: 'border-box !important',
});

const PriorityJournalImportModal = ({
    open,
    onClose,
    previewPriorityJournal,
    commitPriorityJournal,
    createMissingSortCodes,
    direction,
    t,
    formattedSortCodeVatType = [],
    formattedSortCodesGroupNamesOptions = [],
    client,
    token,
    palCodes = [],
    accountCardOptions = [],
    fetchEnums,
    setLoading,
    user,
    setAccountCardOptions
}) => {
    const customStyles = {
        container: (provided) => ({
            ...provided,
            width: '100%',
            boxSizing: 'border-box !important'
        }),
        control: (provided) => ({
            ...provided,
            minHeight: '40px',
            width: '100% !important',
            borderRadius: '8px',
            background: '#F5F5F6',
            border: 'none',
            boxShadow: 'none',
            padding: '0 10px',
            textAlign: 'left',
            boxSizing: 'border-box !important',
            border: '1px solid transparent',
            '&:focus-within': {
                border: '2px solid var(--Foundation-Blue-Normal, #304FFF)',
            },
        }),
        placeholder: (provided) => ({
            ...provided,
            textAlign: 'left',
            color: 'black'
        }),
        singleValue: (provided) => ({
            ...provided,
            textAlign: 'left',
            color: 'black',
        }),
        menu: (provided) => ({
            ...provided,
            textAlign: 'left',
            color: 'black',
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected
                ? '#F9AA2A'
                : state.isFocused
                    ? '#ebebeb'
                    : null,
            color: 'black',
        }),
        dropdownIndicator: (provided, state) => ({
            ...provided,
            color: state.isFocused ? 'darkgray' : 'black',
            '&:hover': {
                color: 'darkgray',
            },
        }),
        menuPortal: (provided) => ({
            ...provided,
            zIndex: 2002,
        }),
    };

    const [journalFile, setJournalFile] = useState(null);
    const [transactionTypesFile, setTransactionTypesFile] = useState(null);
    const [previewDone, setPreviewDone] = useState(false);
    const [processing, setProcessing] = useState(false);

    const [missingSortCodes, setMissingSortCodes] = useState([]);
    const [missingAccountCards, setMissingAccountCards] = useState([]);
    const [editableMissing, setEditableMissing] = useState([]);
    const [parsedRows, setParsedRows] = useState(0);

    // Bulk edit state
    const [selectedRows, setSelectedRows] = useState([]);
    const [bulkVatType, setBulkVatType] = useState(null);
    const [bulkGroup, setBulkGroup] = useState(null);
    const [bulkPar, setBulkPar] = useState(null);
    const [bulkVat, setBulkVat] = useState(null);

    const handleJournalFileDrop = files => {
        if (files?.length) setJournalFile(files[0]);
    };

    const handleTransactionTypesFileDrop = files => {
        if (files?.length) setTransactionTypesFile(files[0]);
    };

    const handlePreview = async () => {
        if (!journalFile) return;

        try {
            setProcessing(true);
            setPreviewDone(false);

            const res = await previewPriorityJournal(journalFile, transactionTypesFile);

            const missing = res?.missingSortCodes ?? [];
            const missingCards = res?.missingAccountCards ?? [];
            const rows = res?.parsedRows ?? 0;

            setMissingSortCodes(missing);
            setMissingAccountCards(missingCards);
            setParsedRows(rows);

            // Normalize missing sortcodes
            setEditableMissing(missing.map(sc => ({
                ...sc,
                sortCodeId: sc.sortCodeId || '',
                sortCodeName: sc.sortCodeName || '',
                sortCodeVatType: sc.sortCodeVatType ?? null,
                participatesInProfitAndLoss: sc.participatesInProfitAndLoss ?? null,
                vat: sc.vat ?? null
            })));

            setPreviewDone(true);
        } catch (err) {
            console.error('Priority journal preview failed:', err);
            setPreviewDone(false);
        } finally {
            setProcessing(false);
        }
    };

    const handleCreateAndCommit = async () => {
        if (!journalFile) return;

        try {
            setProcessing(true);

            // Create missing sortcodes if any
            if (editableMissing && editableMissing.length > 0) {
                await createMissingSortCodes(editableMissing);
            }

            // Import journal entries
            const res = await commitPriorityJournal(journalFile, transactionTypesFile);

            const errorCount = res?.errorCount ?? res?.[1] ?? 0;
            const errorMessages = res?.errorMessages ?? res?.[2] ?? [];

            if (errorCount > 0 && errorMessages.length > 0) {
                console.error('Import errors:', errorMessages);
            } else {
                handleClose();
            }
        } catch (err) {
            console.error('Priority journal commit error:', err);
        } finally {
            setProcessing(false);
        }
    };

    const updateMissing = (index, field, value) =>
        setEditableMissing(prev => {
            const arr = [...prev];
            arr[index] = { ...arr[index], [field]: value };
            return arr;
        });

    const applyBulk = () => {
        setEditableMissing(prev =>
            prev.map(r =>
                selectedRows.includes(r.sortCodeId)
                    ? {
                        ...r,
                        sortCodeVatType: bulkVatType?.value ?? r.sortCodeVatType,
                        sortCodeGroup: bulkGroup?.value ?? r.sortCodeGroup,
                        participatesInProfitAndLoss: bulkPar?.value ?? r.participatesInProfitAndLoss,
                        vat: bulkVat?.value ?? r.vat,
                    }
                    : r
            )
        );
        setSelectedRows([]);
        setBulkVatType(null);
        setBulkGroup(null);
        setBulkPar(null);
        setBulkVat(null);
    };

    const missingIncomplete = useMemo(
        () => editableMissing.some(sc =>
            !sc.sortCodeName ||
            !sc.sortCodeName.trim() ||
            sc.sortCodeVatType === null ||
            sc.sortCodeVatType === undefined ||
            sc.participatesInProfitAndLoss === null ||
            sc.participatesInProfitAndLoss === undefined ||
            sc.vat === null ||
            sc.vat === undefined
        ),
        [editableMissing]
    );

    const handleClose = () => {
        setJournalFile(null);
        setTransactionTypesFile(null);
        setPreviewDone(false);
        setMissingSortCodes([]);
        setMissingAccountCards([]);
        setEditableMissing([]);
        setParsedRows(0);
        setSelectedRows([]);
        onClose();
    };

    if (!open) return null;

    return (
        <div className="download-modal-overlay" onClick={handleClose} style={{ zIndex: 2000 }}>
            <div className="download-modal" onClick={e => e.stopPropagation()} style={{ zIndex: 2001 }}>
                <div className={`download-modal-content ${direction}`} style={{ position: 'relative', maxWidth: previewDone ? '1400px' : '600px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                        <h4 className="report-title">
                            {t('ImportPriorityJournalEntries') || 'ייבוא פקודות יומן מ-Priority'}
                        </h4>
                        <IconButton
                            className="client-back-icon"
                            onClick={handleClose}
                            sx={{ padding: '4px' }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        {/* Journal File Upload */}
                        <div>
                            <h5>{t('JournalEntriesFile') || 'קובץ פקודות יומן'} (.xlsx/.xls)</h5>
                            <DropzoneArea
                                key={journalFile ? journalFile.name : 'priority-journal-empty'}
                                onDrop={handleJournalFileDrop}
                                accept=".xls,.xlsx"
                                filesLimit={1}
                            />
                            {journalFile && (
                                <div style={{ marginTop: 8 }}>
                                    <span>{t('FileSelected')}: {journalFile.name}</span>
                                </div>
                            )}
                        </div>

                        {/* Transaction Types File Upload (Optional) */}
                        <div>
                            <h5>{t('TransactionTypesFile') || 'קובץ סוגי תנועות'} (.xlsx/.xls) - {t('Optional') || 'אופציונלי'}</h5>
                            <DropzoneArea
                                key={transactionTypesFile ? transactionTypesFile.name : 'priority-trans-types-empty'}
                                onDrop={handleTransactionTypesFileDrop}
                                accept=".xls,.xlsx"
                                filesLimit={1}
                            />
                            {transactionTypesFile && (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
                                    <span>{t('FileSelected')}: {transactionTypesFile.name}</span>
                                    <Button size="small" variant="text" onClick={() => setTransactionTypesFile(null)}>{t('Remove')}</Button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: 15, marginTop: 20 }}>
                        <Button
                            variant="contained"
                            onClick={handlePreview}
                            disabled={!journalFile || processing}
                        >
                            {t('Preview')}
                        </Button>
                        <Button
                            variant="contained"
                            color="success"
                            disabled={!previewDone || processing || missingIncomplete || missingAccountCards.length > 0}
                            onClick={handleCreateAndCommit}
                        >
                            {processing ? t('Processing') : t('CreateAndImport')}
                        </Button>
                    </div>

                    {previewDone && (
                        <div style={{ marginTop: 25, maxHeight: 800, overflow: 'auto' }}>
                            <div style={{ marginBottom: 15 }}>
                                <p style={{ fontSize: '14px', color: '#666' }}>
                                    {t('ParsedRows') || 'שורות שנקראו'}: <strong>{parsedRows}</strong>
                                </p>
                            </div>

                            {/* Missing Sortcodes Table */}
                            {editableMissing.length === 0 ? (
                                <p style={{ color: 'green' }}>{t('NoMissingSortCodes')}</p>
                            ) : (
                                <>
                                    <h5>{t('MissingSortCodes')}</h5>
                                    {/* Bulk Edit Controls */}
                                    <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
                                        <Select
                                            placeholder={t('SelectSortCodeVatType')}
                                            options={formattedSortCodeVatType}
                                            value={bulkVatType}
                                            onChange={setBulkVatType}
                                            styles={customStyles}
                                        />
                                        <Select
                                            placeholder={t('SelectSortCodeGroupName')}
                                            options={formattedSortCodesGroupNamesOptions}
                                            value={bulkGroup}
                                            onChange={setBulkGroup}
                                            styles={customStyles}
                                        />
                                        <Select
                                            placeholder={t('participatesInProfitAndLoss')}
                                            options={[{ label: t('Yes'), value: true }, { label: t('No'), value: false }]}
                                            value={bulkPar}
                                            onChange={setBulkPar}
                                            isSearchable={false}
                                            styles={customStyles}
                                        />
                                        <Select
                                            placeholder={t('VAT')}
                                            options={Constants.VATPercentage.map(v => ({ label: `${v}%`, value: v }))}
                                            value={bulkVat}
                                            onChange={setBulkVat}
                                            isSearchable={false}
                                            styles={customStyles}
                                        />
                                        <BorderBoxButton
                                            variant="outlined"
                                            disabled={!bulkVatType && !bulkGroup && !bulkPar && !bulkVat}
                                            onClick={applyBulk}
                                        >
                                            {t('ApplyToSelected')}
                                        </BorderBoxButton>
                                    </div>

                                    {/* Sortcode Table */}
                                    <table className="sortcode-table editable">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '5%' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedRows.length === editableMissing.length && editableMissing.length > 0}
                                                        onChange={e => setSelectedRows(e.target.checked ? editableMissing.map(r => r.sortCodeId) : [])}
                                                    />
                                                </th>
                                                <th>{t('SortCode Id')}</th>
                                                <th>{t('SortCode Name')}</th>
                                                <th>{t('SelectSortCodeVatType')}</th>
                                                <th>{t('SelectSortCodeGroupName')}</th>
                                                <th>{t('participatesInProfitAndLoss')}</th>
                                                <th>{t('VAT')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {editableMissing.map((sc, i) => (
                                                <tr key={sc.sortCodeId}>
                                                    <td>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedRows.includes(sc.sortCodeId)}
                                                            onChange={e => setSelectedRows(prev => e.target.checked ? [...prev, sc.sortCodeId] : prev.filter(id => id !== sc.sortCodeId))}
                                                        />
                                                    </td>
                                                    <td>
                                                        <TextField
                                                            fullWidth
                                                            value={sc.sortCodeId}
                                                            variant="standard"
                                                            InputProps={{
                                                                readOnly: true,
                                                                disableUnderline: true,
                                                                sx: {
                                                                    height: '48px',
                                                                    padding: '0 10px',
                                                                    borderRadius: '8px',
                                                                    background: '#F5F5F6',
                                                                    border: '1px solid transparent'
                                                                }
                                                            }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <TextField
                                                            fullWidth
                                                            value={sc.sortCodeName || ''}
                                                            variant="standard"
                                                            onChange={e => updateMissing(i, 'sortCodeName', e.target.value)}
                                                            placeholder={t('EnterSortCodeName') || 'Enter sortcode name'}
                                                            InputProps={{
                                                                disableUnderline: true,
                                                                sx: {
                                                                    height: '48px',
                                                                    padding: '0 10px',
                                                                    borderRadius: '8px',
                                                                    background: (!sc.sortCodeName || !sc.sortCodeName.trim()) ? 'white' : '#F5F5F6',
                                                                    border: (!sc.sortCodeName || !sc.sortCodeName.trim()) ? '1.5px solid #f44336' : '1px solid transparent',
                                                                },
                                                            }}
                                                            inputProps={{
                                                                style: {
                                                                    textAlign: direction === 'ltr' ? 'left' : 'right',
                                                                },
                                                            }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <Select
                                                            placeholder={t('SelectSortCodeVatType')}
                                                            options={formattedSortCodeVatType}
                                                            value={formattedSortCodeVatType.find(opt => opt.value === sc.sortCodeVatType?.toString()?.trim()) || null}
                                                            onChange={opt => updateMissing(i, 'sortCodeVatType', opt.value)}
                                                            styles={{
                                                                ...customStyles,
                                                                control: (provided) => ({
                                                                    ...customStyles.control(provided),
                                                                    border: (sc.sortCodeVatType !== null && sc.sortCodeVatType !== undefined)
                                                                        ? '1px solid transparent'
                                                                        : '1.5px solid #f44336'
                                                                })
                                                            }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <Select
                                                            placeholder={t('SelectSortCodeGroupName')}
                                                            options={formattedSortCodesGroupNamesOptions}
                                                            value={formattedSortCodesGroupNamesOptions.find(opt => opt.value === sc.sortCodeGroup) || null}
                                                            onChange={opt => updateMissing(i, 'sortCodeGroup', opt ? opt.value : null)}
                                                            styles={customStyles}
                                                            isClearable={true}
                                                        />
                                                    </td>
                                                    <td>
                                                        <Select
                                                            placeholder={t('participatesInProfitAndLoss')}
                                                            options={[{ label: t('Yes'), value: true }, { label: t('No'), value: false }]}
                                                            value={sc.participatesInProfitAndLoss === true ? { label: t('Yes'), value: true } : sc.participatesInProfitAndLoss === false ? { label: t('No'), value: false } : null}
                                                            onChange={opt => updateMissing(i, 'participatesInProfitAndLoss', opt.value)}
                                                            styles={{
                                                                ...customStyles,
                                                                control: provided => ({
                                                                    ...customStyles.control(provided),
                                                                    border: sc.participatesInProfitAndLoss !== undefined && sc.participatesInProfitAndLoss !== null ? '1px solid transparent' : '1.5px solid #f44336'
                                                                })
                                                            }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <Select
                                                            placeholder={t('VAT')}
                                                            options={Constants.VATPercentage.map(v => ({ label: `${v}%`, value: v }))}
                                                            value={Constants.VATPercentage.map(v => ({ label: `${v}%`, value: v })).find(opt => opt.value === sc.vat) || null}
                                                            onChange={opt => updateMissing(i, 'vat', opt.value)}
                                                            styles={{
                                                                ...customStyles,
                                                                control: provided => ({
                                                                    ...customStyles.control(provided),
                                                                    border: sc.vat !== undefined && sc.vat !== null ? '1px solid transparent' : '1.5px solid #f44336'
                                                                })
                                                            }}
                                                            isSearchable={false}
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </>
                            )}

                            {/* Missing Account Cards Table */}
                            {missingAccountCards.length > 0 && (
                                <div style={{ marginTop: 25 }}>
                                    <MdbMissingAccountCardsTable
                                        missingAccountCards={missingAccountCards.map(cardId => ({ cardId, cardName: '' }))}
                                        direction={direction}
                                        client={client}
                                        token={token}
                                        palCodes={palCodes || []}
                                        accountCardOptions={accountCardOptions}
                                        fetchEnums={fetchEnums}
                                        setLoading={setLoading}
                                        isRange={false}
                                        isClosedReportingMonth={false}
                                        sortCodes={[]}
                                        user={user}
                                        setAccountCardOptions={setAccountCardOptions}
                                        onCardCreated={async () => {
                                            if (journalFile) {
                                                await handlePreview();
                                            }
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PriorityJournalImportModal;

